"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const core_1 = require("@angular/core");
const material_1 = require("@angular/material");
const router_1 = require("@angular/router");
const BaseComponent_1 = require("@src/app/commons/BaseComponent");
const map_service_1 = require("@src/app/commons/map.service");
const plugin_manager_service_1 = require("@src/app/service/plugin-manager.service");
const await_class_1 = require("@src/app/xs/controllers/await.class");
const operators_1 = require("rxjs/operators");
const application_events_service_1 = require("../../events/application-events.service");
const ui_manager_service_1 = require("../ui-manager.service");
const Cesium = window.Cesium;
class TileProfilerComponent extends BaseComponent_1.BaseComponent {
    constructor(router, snackBar, dialog, uiManager, applicationEvents, pluginManager, mapService) {
        super(router, snackBar, dialog);
        this.router = router;
        this.snackBar = snackBar;
        this.dialog = dialog;
        this.uiManager = uiManager;
        this.applicationEvents = applicationEvents;
        this.pluginManager = pluginManager;
        this.mapService = mapService;
        this.profilerActive = false;
        this.movementHandler = {};
        this.planePos = { x: 0.0, y: 0.0 };
        this.cartesians = { x: new Cesium.Cartesian3(1.0, 0.0, 0.0), y: new Cesium.Cartesian3(0.0, 0.0, -1.0) };
        this.planeEntities = [];
        this.selectedAxis = 'x';
    }
    start(transforms) {
        console.log('starting profiler', transforms);
        if (transforms) {
            this.queryTransforms(transforms).subscribe((transforms) => {
                this.primitives = this.mapService.viewer.scene.primitives._primitives;
                this.transforms = transforms;
                this.setupLayerSelector();
                this.setupMovementHandlers();
                this.setupClippingPlanes();
                this.profilerActive = true;
            });
        }
        else {
            console.warn('Unable to start Tile Profiler');
        }
    }
    stop() {
        // TODO: Clean everything up.
        if (this.profilerActive) {
            console.log('stopping profiler');
            this.destroyAllPlanes();
            this.destroyMovementHandlers();
            this.profilerActive = false;
        }
    }
    waitForReady() {
        return new Promise(() => {
            await_class_1.XsAwait.until(['mapService.viewer.scene', 'mapService.scene', 'mapService.currentProject'], this).then(() => {
                return setTimeout(() => {
                    // console.log('map service current project and scene ready!');
                }, 1);
            });
        });
    }
    updateTransforms(transforms) {
        this.waitForReady().then(() => {
            // console.log('updating transforms', transforms);
        });
    }
    updateSelectedLayer(layer) {
        // console.log('updating selected layer', layer['transform_id']);
    }
    updatePlane(plane, axis = this.selectedAxis) {
        if (plane.distance !== this.planePos[axis]) {
            plane.distance = this.planePos[axis];
        }
        return plane;
    }
    queryTransforms(transforms) {
        return transforms.children.queryObservable().pipe(operators_1.map((results) => {
            const transforms = results.items;
            const canProfile = (t) => t['fdm_features'].canHeightAdjust;
            return transforms.filter((t) => canProfile(t));
        }));
    }
    setupLayerSelector() {
        this.layerOptions = this.primitives
            .filter((p) => p.transform_id)
            .map((p) => {
            const correspondingTransform = this.transforms.find((t) => t.id === p.transform_id);
            return { label: correspondingTransform.name, layer: p };
        });
        /* Uncomment the below condition to set a layer on init. */
        // if (this.layerOptions.length) {
        //   this.selectedLayer = this.layerOptions[0].layer;
        // }
    }
    setupMovementHandlers() {
        console.log('setting up movement handlers');
        // Grabbing the plane when left mouse down.
        this.movementHandler.down = new Cesium.ScreenSpaceEventHandler(this.mapService.viewer.scene.canvas);
        this.movementHandler.down.setInputAction((movement) => {
            const selectedObject = this.mapService.viewer.scene.pick(movement.position);
            if (Cesium.defined(selectedObject) && Cesium.defined(selectedObject.id) && Cesium.defined(selectedObject.id.plane)) {
                this.selectedPlane = this.planeEntities.find((pe) => pe.id === selectedObject.id._id).plane;
                this.selectedPlane.material = Cesium.Color.YELLOW.withAlpha(0.05);
                this.selectedPlane.outlineColor = Cesium.Color.YELLOW;
                this.mapService.viewer.scene.screenSpaceCameraController.enableInputs = false;
            }
        }, Cesium.ScreenSpaceEventType.LEFT_DOWN);
        // Releasing the plane when left mouse up.
        this.movementHandler.up = new Cesium.ScreenSpaceEventHandler(this.mapService.viewer.scene.canvas);
        this.movementHandler.up.setInputAction(() => {
            if (Cesium.defined(this.selectedPlane)) {
                this.selectedPlane.material = Cesium.Color.WHITE.withAlpha(0.1);
                this.selectedPlane.outlineColor = Cesium.Color.WHITE;
                this.selectedPlane = undefined;
            }
            this.mapService.viewer.scene.screenSpaceCameraController.enableInputs = true;
        }, Cesium.ScreenSpaceEventType.LEFT_UP);
        this.updateMouseMovementAxis();
    }
    updateMouseMovementAxis() {
        this.movementHandler.mouse = new Cesium.ScreenSpaceEventHandler(this.mapService.viewer.scene.canvas);
        this.movementHandler.mouse.setInputAction((movement) => {
            if (Cesium.defined(this.selectedPlane)) {
                const delta = movement.startPosition[this.selectedAxis] - movement.endPosition[this.selectedAxis];
                this.planePos[this.selectedAxis] += delta;
            }
        }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);
    }
    destroyMovementHandlers() {
        return Object.values(this.movementHandler)
            .filter((v) => !!v)
            .map((handler) => {
            handler.destroy();
            return handler;
        })
            .every((handler) => handler.isDestroyed());
    }
    setupClippingPlanes() {
        console.log('Setting up clipping planes');
        if (this.selectedLayer) {
            this.updateClippingPlanes();
        }
    }
    updateClippingPlanes() {
        if (this.selectedLayer) {
            this.resetPlanePosition();
            this.removeAllClippingPlanes();
            this.removeAllPlaneEntities();
        }
        this.clippingPlanes = new Cesium.ClippingPlaneCollection({
            planes: [new Cesium.ClippingPlane(this.cartesians[this.selectedAxis], 0.0)],
            edgeWidth: 1.0,
        });
        // console.log('clipping planes before assignment', this.selectedLayer.clippingPlanes);
        this.selectedLayer.clippingPlanes = this.clippingPlanes;
        // console.log('clipping planes after assignment', this.selectedLayer.clippingPlanes);
        const boundingSphere = this.selectedLayer.boundingSphere;
        const radius = boundingSphere.radius;
        // console.log('bounding sphere', boundingSphere);
        // console.log((this.clippingPlanes as any)._planes);
        const planesArray = this.clippingPlanes._planes;
        this.planeEntities = planesArray.map((plane) => {
            return this.mapService.viewer.entities.add({
                position: boundingSphere.center,
                plane: {
                    dimensions: new Cesium.Cartesian2(radius * 1.25, radius * 1.25),
                    material: Cesium.Color.WHITE.withAlpha(0.1),
                    plane: new Cesium.CallbackProperty(() => this.updatePlane(plane), false),
                    outline: true,
                    outlineColor: Cesium.Color.WHITE,
                },
            });
        });
        this.flyToSelectedLayer();
    }
    flyToSelectedLayer() {
        const selectedOption = this.layerOptions.find((l) => l.layer === this.selectedLayer);
        const correspondingTransform = this.transforms.find((t) => t.name === selectedOption.label);
        if (selectedOption && correspondingTransform) {
            this.mapService.helper.sendCameraTo(correspondingTransform);
        }
    }
    updateSelectedAxis() {
        this.updateMouseMovementAxis();
        this.updateClippingPlanes();
    }
    resetPlanePosition() {
        this.planePos = { x: 0.0, y: 0.0 };
    }
    removeAllClippingPlanes() {
        if (this.selectedLayer && this.selectedLayer.clippingPlanes) {
            this.selectedLayer.clippingPlanes.removeAll();
        }
        this.layerOptions
            .map((lo) => lo.layer)
            .forEach((p) => {
            if (p.clippingPlanes) {
                p.clippingPlanes.removeAll();
            }
        });
    }
    removeAllPlaneEntities() {
        // Remove all of the planes drawn to the canvas.
        this.planeEntities.forEach((e) => {
            const viewerEntities = this.mapService.viewer.entities;
            if (viewerEntities && viewerEntities.contains(e)) {
                this.mapService.viewer.entities.remove(e);
            }
        });
    }
    destroyAllPlanes() {
        this.removeAllClippingPlanes();
        this.removeAllPlaneEntities();
        this.resetPlanePosition();
        this.selectedAxis = 'x';
        this.clippingPlanes = new Cesium.ClippingPlaneCollection({ planes: [], enabled: false });
        this.selectedPlane = undefined;
        this.selectedLayer = undefined;
    }
}
exports.TileProfilerComponent = TileProfilerComponent;
